import { Navigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { LoadingScreen } from 'src/components/loading-screen';
import { useStores } from 'src/models';
import { PATH_AUTH, PATH_PAGE } from 'src/routes/paths';

type AuthGuardProps = {
  children: React.ReactNode;
};

// 금연일 설정 > FTND > 문진 > 복용약 문진 > 서비스 이용 가능
const REQUIRE_SURVEY_PATHS = [
  PATH_PAGE.home,
  PATH_PAGE.drJin.root,
  PATH_PAGE.mission.root,
  PATH_PAGE.craving.root,
  PATH_PAGE.calendar.root,
];

const EXCLUDED_PATHS = [
  PATH_PAGE.quitSmokingDateSettings,
  PATH_PAGE.ftnd.root,
  PATH_PAGE.medicalQuestion.root,
  PATH_PAGE.myPage.root,
  PATH_PAGE.drugQuestion.root,
  PATH_PAGE.medicineSettings.root,
];

function AuthGuard({ children }: AuthGuardProps) {
  const { authStore } = useStores();
  const { isAuthenticated, isInitialized } = authStore;

  const { pathname } = useLocation();

  // 온보딩 미완료 시 이동할 경로.
  // useEffect(페인트 이후)에서 navigate 하면 목적지 화면이 한 번 마운트되어
  // 쿼리와 로딩 모달이 떴다 사라지므로, 렌더 단계에서 판단한다.
  const userProfile = authStore.user;
  let surveyRedirectPath: string | null = null;

  if (
    userProfile &&
    !EXCLUDED_PATHS.some((path) => pathname.startsWith(path)) &&
    REQUIRE_SURVEY_PATHS.some((path) => pathname.startsWith(path))
  ) {
    // 문진 완료 여부
    const hasIncompleteMedQuestions = userProfile.medicalQuestionCompleteYn?.some(
      (item) => item.medicalQuestionCompleteYn === 'N',
    );

    // 복용약 문진 완료 여부
    const hasIncompleteDrugQuestions = userProfile.drugQuestionCompleteYn?.some(
      (item) => item.drugQuestionCompleteYn === 'N',
    );

    if (!userProfile.quitDt) {
      surveyRedirectPath = PATH_PAGE.quitSmokingDateSettings;
    } else if (userProfile.ftndCompleteYn === 'N') {
      surveyRedirectPath = PATH_PAGE.ftnd.root;
    } else if (hasIncompleteMedQuestions) {
      surveyRedirectPath = PATH_PAGE.medicalQuestion.root;
    } else if (hasIncompleteDrugQuestions) {
      surveyRedirectPath = PATH_PAGE.drugQuestion.root;
    }
  }

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={PATH_AUTH.login} replace state={{ from: pathname }} />;
  }

  if (surveyRedirectPath && pathname !== surveyRedirectPath) {
    return <Navigate to={surveyRedirectPath} replace />;
  }

  // Page 래핑 제거: children이 이미 자체 <Page> 사용하므로 중첩 렌더 방지
  // (CSnackbar/CInfoModal 중복 렌더로 인한 알림 2회 표시 버그 해결)
  return <>{children}</>;
}

export default observer(AuthGuard);
